import { DOCUMENT, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgbCarousel, NgbSlide } from '@ng-bootstrap/ng-bootstrap';
import { AppImageMultipleQualityComponent } from '@shared/components/app-image-multiple-quality/app-image-multiple-quality.component';
import { ImagePreloadService } from '@shared/services/image-preload.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { Auction } from 'src/app/articles-manager/interfaces/auction.interface';

declare global {
  interface Window {
    CI360: any;
  }
}

@Component({
  selector: 'app-article-gallery',
  templateUrl: './article-gallery.component.html',
  styleUrls: ['./article-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgbCarousel,
    NgIf,
    NgFor,
    NgTemplateOutlet,
    NgbSlide,
    AppImageMultipleQualityComponent,
  ],
})
export class ArticleGalleryComponent implements AfterViewInit {
  selectedAuction = input<Auction>();
  @ViewChild('galleryCarousel') carousel!: NgbCarousel;
  id = signal('id0');
  show360 = signal(false);
  imageWidth = signal(900);
  firstLoaded = signal(false);
  mouseEntered = signal(false);

  photo360 = computed(
    () => this.selectedAuction()?.article?.articlePhoto360List?.length > 0
  );
  frames = computed(
    () => this.selectedAuction()?.article?.articlePhoto360List?.length ?? 1
  );
  mainImage = computed<string | null>(() => {
    if (this.photo360()) {
      return (
        this.selectedAuction().article.articlePhoto360Url +
        this.selectedAuction().article.idArticle +
        '_1.jpg' +
        `?w=${this.imageWidth()}`
      );
    }
    if (this.selectedAuction()?.article?.articlePhotoList[0]?.fullPath) {
      return (
        this.selectedAuction().article.articlePhotoList[0].fullPath +
        `?w=${this.imageWidth()}`
      );
    }

    return null;
  });

  imagePreloadService = inject(ImagePreloadService);
  platformService = inject(PlatformService);
  _change = inject(ChangeDetectorRef);
  private document = inject(DOCUMENT);

  constructor() {
    effect(() => {
      if (this.mainImage()) {
        this.imagePreloadService.preloadImage(this.mainImage());
        this.addPreloadLinkToHead(this.mainImage());
        this.addPreloadLinkToHead(this.mainImage().replace('w=900', 'w=600'));
      }
    });
  }

  /**
   * Adds a preload link to the document head for the main image
   */
  private addPreloadLinkToHead(imageUrl: string): void {
    this.platformService.runOnServer(() => {
      const existingPreload = this.document.querySelector(
        `link[rel="preload"][href="${imageUrl}"]`
      );

      if (!existingPreload) {
        const linkElement = this.document.createElement('link');
        linkElement.rel = 'preload';
        linkElement.href = imageUrl;
        linkElement.as = 'image';
        linkElement.type = 'image/jpeg';

        this.document.head.appendChild(linkElement);
      }
    });
  }

  ngAfterViewInit() {
    this.platformService.runOnBrowser(() => {
      window.matchMedia('(max-width: 700px)').addEventListener('change', () => {
        this.imageWidth.set(
          window.matchMedia('(max-width: 700px)').matches ? 600 : 900
        );

        this._change.markForCheck();
      });

      this.imageWidth.set(
        window.matchMedia('(max-width: 700px)').matches ? 600 : 900
      );
      this.firstLoaded.set(true);
      window.CI360.init();
      this._change.markForCheck();
    });
  }

  public getFullAttachmentPath() {
    return (
      this.selectedAuction().article.idArticle +
      `_{index}.jpg?w=${this.imageWidth()}`
    );
  }

  navigateToSlide(item: string) {
    this.id.set(item);
    this.carousel.select(item);
  }

  mouseEnter() {
    if (!this.mouseEntered()) {
      const viewId = 'cloudimage-360';
      const new360View: any = document.getElementById(viewId);
      new360View.classList.add('cloudimage-360');
      window.CI360.add(viewId);
      this._change.markForCheck();
      setTimeout(() => {
        this.show360.set(true);
        this._change.markForCheck();
      }, 100);
      this.mouseEntered.set(true);
    }
  }

  onSlide(e) {
    this.id.set(e.current);
  }
}
